import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  SimpleGrid,
  Button,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import {
  FaHeart,
  FaUsers,
  FaGamepad,
  FaPaintBrush,
  FaHome,
  FaRocket,
  FaStar,
  FaGift,
  FaHandshake,
  FaLightbulb,
} from 'react-icons/fa';

// CIL Colors - Limitati a giallo, blu e verde
const cilColors = {
  yellow: '#ffcc00',
  teal: '#3eb297',
  sky: '#36a9e1',
  plum: '#3b235a',
};

const HowItWorksModal = ({ isOpen, onClose }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const benefits = [
    {
      icon: FaUsers,
      title: 'Comunità Attiva',
      description:
        'Partecipa a eventi esclusivi e incontra persone con i tuoi stessi interessi',
      color: cilColors.teal,
      emoji: '🤝',
    },
    {
      icon: FaGamepad,
      title: 'Accesso Prioritario',
      description:
        'Riserva per primo i posti ai laboratori e agli eventi più richiesti',
      color: cilColors.sky,
      emoji: '🎮',
    },
    {
      icon: FaPaintBrush,
      title: 'Comunicazioni Dedicati',
      description:
        'Ricevi aggiornamenti esclusivi sui progetti e le iniziative di CIL',
      color: cilColors.teal,
      emoji: '📧',
    },
    {
      icon: FaStar,
      title: 'Coming Soon',
      description: 'Sconti con i commercianti della zona (in arrivo)',
      color: cilColors.yellow,
      emoji: '🛍️',
    },
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Scegli il Piano',
      description: "Seleziona l'importo mensile che preferisci",
      icon: FaHandshake,
      color: cilColors.teal,
    },
    {
      step: '2',
      title: 'Pagamento Sicuro',
      description: 'Usa la tua carta di credito o PayPal',
      icon: FaHeart,
      color: cilColors.sky,
    },
    {
      step: '3',
      title: 'Supporto Continuo',
      description: 'Il tuo contributo si rinnova automaticamente ogni mese',
      icon: FaRocket,
      color: cilColors.teal,
    },
    {
      step: '4',
      title: 'Benefici Immediati',
      description: 'Accedi subito a tutti i vantaggi del tuo piano',
      icon: FaGift,
      color: cilColors.yellow,
    },
  ];

  const impactExamples = [
    {
      amount: '€5/mese',
      impact: 'Un caffè al mese che diventa materiali per laboratori creativi',
      icon: FaPaintBrush,
      color: cilColors.teal,
    },
    {
      amount: '€10/mese',
      impact: 'Un pranzo al mese che diventa supporto per eventi culturali',
      icon: FaGamepad,
      color: cilColors.sky,
    },
    {
      amount: '€20/mese',
      impact: 'Una cena al mese che diventa sostegno per progetti educativi',
      icon: FaUsers,
      color: cilColors.yellow,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={['full', 'xl', '6xl']}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent
        bg={bgColor}
        borderRadius={['0', '0']}
        maxW="1200px"
        mx={[0, 4]}
        boxShadow="2xl"
        border="1px solid"
        borderColor={borderColor}
        h={['100vh', 'auto']}
      >
        <ModalHeader
          textAlign="center"
          bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          color="white"
          borderRadius="2xl 2xl 0 0"
          py={[4, 6, 8]}
          px={[4, 6, 8]}
          position="relative"
          overflow="hidden"
          minH={['120px', '140px', '160px']}
        >
          {/* Elementi decorativi di sfondo - Ridotti su mobile */}
          <Box
            position="absolute"
            top="10%"
            left="5%"
            w={['40px', '60px', '80px']}
            h={['40px', '60px', '80px']}
            borderRadius="50%"
            bg="rgba(255, 255, 255, 0.1)"
            filter="blur(15px)"
          />
          <Box
            position="absolute"
            top="20%"
            right="10%"
            w={['30px', '45px', '60px']}
            h={['30px', '45px', '60px']}
            borderRadius="50%"
            bg="rgba(255, 255, 255, 0.08)"
            filter="blur(12px)"
          />

          <VStack spacing={[2, 3, 4]} position="relative" zIndex={2}>
            <Icon
              as={FaHeart}
              w={[8, 10, 12]}
              h={[8, 10, 12]}
              color={cilColors.yellow}
            />
            <Text
              fontSize={['xl', '2xl', '3xl']}
              fontWeight="bold"
              lineHeight="1.2"
              maxW="full"
              px={2}
            >
              Come Funziona il Sostegno Mensile?
            </Text>
          </VStack>
        </ModalHeader>

        <ModalCloseButton
          color="white"
          bg="rgba(0,0,0,0.3)"
          _hover={{ bg: 'rgba(0,0,0,0.5)' }}
          size="lg"
          borderRadius="full"
          top={6}
          right={6}
          zIndex={10}
        />

        <ModalBody p={[4, 6, 8]}>
          <VStack spacing={[8, 10, 12]}>
            {/* Sezione "Come Funziona" */}
            <Box w="full">
              <Text
                fontSize="2xl"
                fontWeight="bold"
                textAlign="center"
                mb={8}
                color={cilColors.plum}
              >
                🚀 Il Processo in 4 Semplici Passi
              </Text>

              <SimpleGrid columns={[1, 2, 4]} spacing={6}>
                {howItWorks.map((step) => (
                  <Box
                    key={step.step}
                    textAlign="center"
                    p={6}
                    borderRadius="xl"
                    bg="gray.50"
                    border="2px solid"
                    borderColor="transparent"
                    _hover={{
                      borderColor: step.color,
                      transform: 'translateY(-4px)',
                    }}
                    transition="all 0.3s"
                    position="relative"
                  >
                    {/* Badge del numero del passo */}
                    <Badge
                      position="absolute"
                      top="-3"
                      left="50%"
                      transform="translateX(-50%)"
                      bg={step.color}
                      color="white"
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="lg"
                      fontWeight="bold"
                      boxShadow="lg"
                    >
                      {step.step}
                    </Badge>

                    <Icon
                      as={step.icon}
                      w={12}
                      h={12}
                      color={step.color}
                      mb={4}
                      mt={2}
                    />
                    <Text
                      fontWeight="bold"
                      fontSize="lg"
                      mb={2}
                      color={cilColors.plum}
                    >
                      {step.title}
                    </Text>
                    <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                      {step.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* Sezione "Impatto del Tuo Contributo" */}
            <Box w="full">
              <Text
                fontSize="2xl"
                fontWeight="bold"
                textAlign="center"
                mb={8}
                color={cilColors.plum}
              >
                💡 L&apos;Impatto del Tuo Contributo Mensile
              </Text>

              <SimpleGrid columns={[1, 3]} spacing={6}>
                {impactExamples.map((example) => (
                  <Box
                    key={`impact-${example.amount}-${example.impact}`}
                    textAlign="center"
                    p={6}
                    borderRadius="xl"
                    bg="white"
                    border="2px solid"
                    borderColor={example.color}
                    boxShadow="lg"
                    position="relative"
                    overflow="hidden"
                    _before={{
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      bg: example.color,
                    }}
                  >
                    <Icon
                      as={example.icon}
                      w={10}
                      h={10}
                      color={example.color}
                      mb={3}
                    />
                    <Text
                      fontWeight="bold"
                      fontSize="xl"
                      mb={2}
                      color={example.color}
                    >
                      {example.amount}
                    </Text>
                    <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                      {example.impact}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* Sezione "Cosa Ricevi Tu" */}
            <Box w="full">
              <Text
                fontSize="2xl"
                fontWeight="bold"
                textAlign="center"
                mb={8}
                color={cilColors.plum}
              >
                🤝 Cosa Ricevi Come Sostenitore
              </Text>

              <SimpleGrid columns={[1, 2]} spacing={6}>
                {benefits.map((benefit) => (
                  <Box
                    key={`benefit-${benefit.title}-${benefit.description}`}
                    p={6}
                    borderRadius="xl"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="md"
                    _hover={{ boxShadow: 'lg', transform: 'translateY(-2px)' }}
                    transition="all 0.3s"
                  >
                    <HStack spacing={4} align="flex-start">
                      <Box
                        p={3}
                        borderRadius="full"
                        bg={`${benefit.color}20`}
                        color={benefit.color}
                      >
                        <Icon as={benefit.icon} w={6} h={6} />
                      </Box>
                      <Box flex="1">
                        <HStack spacing={2} mb={2}>
                          <Text
                            fontWeight="bold"
                            fontSize="lg"
                            color={cilColors.plum}
                          >
                            {benefit.title}
                          </Text>
                          <Text fontSize="2xl">{benefit.emoji}</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                          {benefit.description}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* Sezione "Perché è Importante" */}
            <Box w="full" bg="gray.50" p={8} borderRadius="2xl">
              <VStack spacing={6}>
                <Icon as={FaLightbulb} w={12} h={12} color={cilColors.yellow} />
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  textAlign="center"
                  color={cilColors.plum}
                >
                  Perché il Sostegno Mensile è Così Importante?
                </Text>

                <SimpleGrid columns={[1, 3]} spacing={6} w="full">
                  <Box textAlign="center">
                    <Icon
                      as={FaHome}
                      w={10}
                      h={10}
                      color={cilColors.teal}
                      mb={3}
                    />
                    <Text fontWeight="semibold" mb={2} color="gray.700">
                      Stabilità
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Ci permette di pianificare progetti a lungo termine e
                      mantenere spazi stabili
                    </Text>
                  </Box>

                  <Box textAlign="center">
                    <Icon
                      as={FaUsers}
                      w={10}
                      h={10}
                      color={cilColors.sky}
                      mb={3}
                    />
                    <Text fontWeight="semibold" mb={2} color="gray.700">
                      Crescita
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Possiamo investire in nuovi progetti e ampliare le nostre
                      attività
                    </Text>
                  </Box>

                  <Box textAlign="center">
                    <Icon
                      as={FaHeart}
                      w={10}
                      h={10}
                      color={cilColors.sky}
                      mb={3}
                    />
                    <Text fontWeight="semibold" mb={2} color="gray.700">
                      Comunità
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Creiamo legami duraturi e una rete di supporto reciproco
                    </Text>
                  </Box>
                </SimpleGrid>
              </VStack>
            </Box>

            {/* CTA Finale */}
            <Box w="full" textAlign="center">
              <VStack spacing={6}>
                <Text fontSize="xl" fontWeight="bold" color={cilColors.plum}>
                  Pronto a Sostenere la Nostra Associazione?
                </Text>
                <Text fontSize="lg" color="gray.600" maxW="600px">
                  Ogni contributo mensile, grande o piccolo, ci aiuta a
                  mantenere vivi gli spazi di CIL e a creare progetti per la
                  comunità.
                </Text>

                <HStack spacing={4} flexWrap="wrap" justify="center">
                  <Button
                    size="lg"
                    bg={cilColors.yellow}
                    color={cilColors.plum}
                    _hover={{ bg: '#e6b800', transform: 'scale(1.05)' }}
                    px={8}
                    py={4}
                    fontWeight="bold"
                    onClick={() => {
                      onClose();
                      // Scroll alla sezione subscription plans
                      document
                        .getElementById('subscription-plans')
                        ?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Sostieni CIL
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    borderColor={cilColors.teal}
                    color={cilColors.teal}
                    _hover={{ bg: cilColors.teal, color: 'white' }}
                    px={8}
                    py={4}
                    onClick={onClose}
                  >
                    Chiudi
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default HowItWorksModal;
